import { z } from 'zod'

/**
 * In-app notifications (ADR-021): the bell in the top bar. A notification stores ids and enums only;
 * its text is composed when it is read, and one whose subject the reader can no longer see is not
 * shown, so removing someone from a record stops its content reaching them.
 */

export const NOTIFICATION_KINDS = [
  /** You were made the assignee. */
  'TASK_ASSIGNED',
  /** The task was given to someone else (to its previous assignee). */
  'TASK_UNASSIGNED',
  /** New unassigned work in a team you lead. */
  'TASK_QUEUED',
  /** Submitted for your review. */
  'TASK_REVIEW_REQUESTED',
  /** Your reviewer returned the work with feedback. */
  'TASK_RETURNED',
  /** Work you requested was completed. */
  'TASK_COMPLETED',
  'TASK_CANCELLED',
  'TASK_REOPENED',
  'TASK_COMMENTED',
  'TASK_DUE_CHANGED',
  /** The assignee asks for a new due date. */
  'TASK_DATE_REQUESTED',
  /** A blocker you follow up on was recorded. */
  'TASK_BLOCKED',
  'TASK_UNBLOCKED',
  /** You were added as a contributor or follower. */
  'TASK_PERSON_ADDED',
  'TASK_DUE_TODAY',
  'TASK_OVERDUE',
  /** A task of a team you lead is overdue beyond the company's escalation threshold. */
  'TASK_OVERDUE_ESCALATED',
  /** Today is the follow-up date of a blocker you own. */
  'TASK_FOLLOW_UP_DUE',
  /** A visit was booked for your team (you lead it, or you are in the team). */
  'SERVICE_VISIT_SCHEDULED',
  'SERVICE_VISIT_RESCHEDULED',
  'SERVICE_VISIT_CANCELLED',
  /** A technician asks for another visit (to those who approve follow-ups). */
  'SERVICE_FOLLOW_UP_REQUESTED',
  'SERVICE_FOLLOW_UP_APPROVED',
  'SERVICE_FOLLOW_UP_RETURNED',
  /** Money collected on site awaits verification (to the accountants). */
  'SERVICE_COLLECTION_REPORTED',
  'SERVICE_COLLECTION_VERIFIED',
  'SERVICE_COLLECTION_REJECTED',
  /**
   * C3: a technician found an issue on an asset and opened a fault job (to service.schedule holders; subject
   * the new job). Data `{assetId, assetTag, jobNumber, siteName}`.
   */
  'SERVICE_CHECK_ISSUE',
  /**
   * Maintenance contracts (C2–C4). Subject `service_contract` unless said otherwise. Data carries the
   * contract's `number` and `customerName` plus:
   * - RENEWAL_DUE `{endDate, daysLeft}` at each notice day (contracts.manage), once per day count;
   * - EXPIRED `{endDate}` (contracts.manage), once;
   * - BILLING_READY `{count, total}` per contract and day (contracts.bill);
   * - VISITS_PLANNED `{count, siteCount}` per contract and generator run (service.schedule);
   * - VISIT_OVERDUE `{jobNumber, dueDate, siteName}` once per occurrence (service.schedule; subject the job);
   * - ASSET_WARRANTY_EXPIRING `{assetId, assetTag, warrantyUntil, siteName}` once per asset and date
   *   (service.assets; subject the asset's open contract, else none is sent).
   */
  'CONTRACT_RENEWAL_DUE',
  'CONTRACT_EXPIRED',
  'CONTRACT_BILLING_READY',
  'CONTRACT_VISITS_PLANNED',
  'CONTRACT_VISIT_OVERDUE',
  'ASSET_WARRANTY_EXPIRING',
  /** A personal-data request is due in 10, 5 or 2 working days (to its assignee, else every privacy.dsr holder). */
  'DSR_DUE_SOON',
  /** A personal-data request is past its legal deadline (daily until it is closed). */
  'DSR_OVERDUE',
  /** You were added to a project's team. */
  'PROJECT_MEMBER_ADDED',
  /** You were made a project's manager. */
  'PROJECT_MANAGER_ASSIGNED',
  /** A project you are on was activated, put on hold, resumed, completed, closed, reopened or cancelled. */
  'PROJECT_STATUS_CHANGED',
  /**
   * A project's budget use reached the warning or the critical level (to its manager and the
   * projects.budgets holders who reach it; once per level and budget version).
   */
  'PROJECT_BUDGET_THRESHOLD',
  /** A budget waits for your approval (four-eyes path; workflow runs notify through their tasks). */
  'PROJECT_BUDGET_SUBMITTED',
  /** A budget you prepared or submitted was approved and is now the baseline (also to the manager). */
  'PROJECT_BUDGET_APPROVED',
  /**
   * A billing plan item is ready to invoice (its date came, its milestone was achieved or it was marked
   * ready), once per item, to the manager and the projects.bill holders who reach the project.
   */
  'PROJECT_BILLING_READY',
  /** A progress claim was submitted to the client (to the project's managers and certifiers). */
  'PROJECT_CLAIM_SUBMITTED',
  /** A progress claim was certified (its invoice is issued), rejected or voided (to the manager and the QS). */
  'PROJECT_CLAIM_CERTIFIED',
  'PROJECT_CLAIM_REJECTED',
  'PROJECT_CLAIM_VOIDED',
  /** The client approved or rejected a variation (to the manager and its author). */
  'PROJECT_VARIATION_DECIDED',
  /**
   * Internal approval, four-eyes path (P5; workflow runs notify through their tasks): a variation or claim
   * waits for a projects.contract_approve holder who reaches the project (not its submitter); or such a
   * holder returned it to DRAFT (to its submitter). Data `{subject: 'variation' | 'claim', recordId, number,
   * title? (variations), claimKind? and periodTo? (claims), projectId, projectCode, projectName, reason?}`.
   */
  'PROJECT_CONTRACT_APPROVAL_PENDING',
  'PROJECT_CONTRACT_APPROVAL_RETURNED',
  /**
   * Time (P6): someone submitted time on a project (to the approvers who reach it, once per submit and
   * project), or an approver returned entries (to their person). Data `{projectId, projectCode,
   * projectName, userName, weekStart, count, minutes, reason?}`.
   */
  'PROJECT_TIME_SUBMITTED',
  'PROJECT_TIME_RETURNED',
  /**
   * AI (P8): the company's monthly tokens reached 80 % or 100 % of its limit (to ai.manage holders, once
   * per month and level). Data `{pct, month, used, limit}`.
   */
  'AI_BUDGET_THRESHOLD',
  /**
   * AA1: the weekly Books health summary (Sunday 07:00 Muscat) to accounts.manage and journals.post holders,
   * only with warning or critical findings. Data `BooksHealthDigestData`.
   */
  'BOOKS_HEALTH_DIGEST',
  /**
   * A project signal is new or escalated (05 §7.4, P5): to the people `SIGNAL_DEFS` names, within scope,
   * once per code, scope key, severity and period; past the daily cap the rest come as one digest.
   */
  'PROJECT_SIGNAL',
  'PROJECT_SIGNAL_DIGEST',
  /**
   * A guarantee reaches a reminder day before expiry, or has expired (P5; to the project's manager and the
   * projects.contract holders reaching it). Data `{guaranteeId, type, direction, reference (the bank's
   * number), expiryDate, projectCode, projectName}`, plus `mark` and `daysToExpiry` on EXPIRING.
   */
  'PROJECT_GUARANTEE_EXPIRING',
  'PROJECT_GUARANTEE_EXPIRED',
  /**
   * The nightly contracting reconciliation found a difference (to ledger.read + projects.financials
   * holders). Data `{identity, asOf, projectCode, projectName}`: one per identity, project and day; a
   * difference no project carries (postings on a contracting control account traced to no project) comes
   * once per identity and day, with `projectCode` and `projectName` null, to those whose
   * projects.financials reaches every project.
   */
  'PROJECT_RECONCILIATION_DIFFERENCE',
  /**
   * ADR-046: the day's cheques to follow up (07:30 Muscat), once a day per direction, to the holders of
   * receivedCheques.manage or issuedCheques.manage. Data `ChequesDueData`.
   */
  'CHEQUES_DUE',
  /**
   * ADR-048: employee documents (civil ID, resident card, passport, visa, work permit…) reaching 30, 7 or 0
   * days before their expiry today (07:00 Muscat), once a day, to holders of employees.documents whose
   * employee scope is ALL. Data `EmployeeDocumentsExpiringData`.
   */
  'EMPLOYEE_DOCUMENTS_EXPIRING',
] as const
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number]

/** Record types a notification may be about (each module registers how to show its own). */
export const NOTIFICATION_SUBJECT_TYPES = [
  'task',
  'service_job',
  /** C2: a maintenance contract (shown to contracts.read holders). */
  'service_contract',
  'purchase',
  'privacy_dsr',
  'project',
  /**
   * P8: the company's AI usage (`AI_BUDGET_THRESHOLD`); the subject id is the company's id. Shown to
   * ai.manage holders only.
   */
  'ai_usage',
  /**
   * AA1: the company's books (`BOOKS_HEALTH_DIGEST`); the subject id is the company's id. Shown to holders of
   * ledger.read or reports.financial (the Books health page).
   */
  'books_health',
  /**
   * ADR-046: the company's cheques (`CHEQUES_DUE`); the subject id is the company's id. Shown to those who
   * manage received or issued cheques.
   */
  'cheques',
  /**
   * ADR-048: the company's employee documents (`EMPLOYEE_DOCUMENTS_EXPIRING`); the subject id is the company's
   * id. Shown to those who read every employee.
   */
  'employee_documents',
] as const
export type NotificationSubjectType = (typeof NOTIFICATION_SUBJECT_TYPES)[number]

export const notificationListQuerySchema = z.object({
  filter: z.enum(['all', 'unread']).default('all'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
})
export type NotificationListQuery = z.input<typeof notificationListQuerySchema>

export type NotificationData = Record<string, string | number | boolean | null>

export interface NotificationItem {
  id: string
  kind: NotificationKind
  createdAt: string
  readAt: string | null
  actor: { id: string; name: string; nameAr: string | null } | null
  subject: {
    type: NotificationSubjectType
    id: string
    /** The record's number (TSK-00042). */
    number: string
    /** Its title as the reader may see it now. */
    title: string
  }
  data: NotificationData
}

export interface NotificationPage {
  items: NotificationItem[]
  page: number
  pageSize: number
  total: number
  unread: number
}

export interface NotificationCount {
  unread: number
}

/**
 * `EMPLOYEE_DOCUMENTS_EXPIRING` data: how many documents reach a reminder day today (`entering`), and the
 * documents of current employees already expired, due within 7 days and due within 30 days.
 */
export interface EmployeeDocumentsExpiringData {
  entering: number
  expired: number
  within7: number
  within30: number
}
